import React from 'react'
import PropTypes from 'prop-types'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { View, StyleSheet } from 'react-native'
import { Txt, Button, TimeZoneSelect } from '../../../../components'
import InputFormik from '../../../../components/InputFormik'
import { usePlatforms } from '../../../../hooks/usePlatforms'
import { useIsDemo } from '../../../../hooks/useIsDemo'

const validationSchema = Yup.object({
  name: Yup.string()
    .required('Name is required')
    .nullable(),
  email: Yup.string()
    .email('Invalid email address')
    .nullable(),
  phone_number: Yup.string()
    .matches('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\\s\\./0-9]*$', 'Invalid phone number')
    .nullable()
})

export const setPayload = ({ email = '', name = '', phone_number = '', timezone }) => ({
  email,
  name,
  phone_number,
  timezone_id: timezone?.timezone_id || ''
})

const UserInformationForm = ({ load, user, onSubmit, handleModal }) => {
  const { isTablet } = usePlatforms()
  const styles = useStyles(isTablet)
  const isDemo = useIsDemo()

  const isFormUntouched = (values) => {
    return (values.email || '') === (user?.email || '') && (values.phone_number || '') === (user?.phone_number || '') && (values.name || '') === (user?.name || '') && JSON.stringify(values.timezone || '') === JSON.stringify(user?.timezone || '')
  }

  return (
    <>
      <Formik
        initialValues={user || {}}
        validationSchema={validationSchema}
        onSubmit={values => onSubmit(setPayload(values))}
      >
        {({ values, errors, touched, setFieldValue, handleSubmit }) => (
          <View style={styles.container}>
            <Txt
              Key='myProfilePersonalInformationTitle'
              text='Personal Information'
              identity='subtitle'
              color='SLATE_GRAY'
              style={styles.subtitle}
            />
            <View style={styles.inputsContainer}>
              <View style={styles.inputs}>
                <InputFormik
                  name='name'
                  onChange={setFieldValue}
                  value={values?.name || ''}
                  error={touched.name && errors.name}
                  placeholder='Name'
                  letfIcon='User'
                  testID='profileUserNameInput'
                  accessibilityLabel='profileUserNameInput'
                  editable={!isDemo}
                />
              </View>
              <View style={styles.inputs}>
                <InputFormik
                  name='email'
                  placeholder='Email'
                  letfIcon='Email'
                  value={values?.email || ''}
                  error={touched.email && errors.email}
                  onChange={setFieldValue}
                  keyboardType='email-address'
                  testID='profileUserEmailInput'
                  accessibilityLabel='profileUserEmailInput'
                  editable={!isDemo}
                />
              </View>
              <View style={styles.inputs}>
                <InputFormik
                  name='phone_number'
                  placeholder='Phone'
                  letfIcon='Phone'
                  value={values?.phone_number || ''}
                  error={touched.phone_number && errors.phone_number}
                  onChange={setFieldValue}
                  keyboardType='phone-pad'
                  testID='profileUserPhoneInput'
                  accessibilityLabel='profileUserPhoneInput'
                  editable={!isDemo}
                />
              </View>
              <View style={styles.inputs}>
                <TimeZoneSelect
                  name='timezone'
                  selected={values?.timezone}
                  onChange={setFieldValue}
                  readonly={isDemo}
                />
              </View>
            </View>
            <View style={styles.buttons}>
              <Button
                title='Change Password'
                containerStyle={styles.passwordButton}
                style={styles.passwordButton}
                onPress={handleModal}
                type='outlined'
                testID='profileUserChangePasswordButton'
                accessibilityLabel='profileUserChangePasswordButton'
                disabled={isDemo}
              />
              <Button
                title='Save'
                containerStyle={styles.saveButton}
                onPress={handleSubmit}
                loading={load}
                testID='profileUserSaveButton'
                accessibilityLabel='profileUserSaveButton'
                disabled={isDemo || isFormUntouched(values)}
              />
            </View>
          </View>
        )}
      </Formik>
    </>
  )
}

const useStyles = (isTablet) => StyleSheet.create({
  container: {
    paddingHorizontal: 20
  },
  inputsContainer: {
    flex: 1
  },
  inputs: {
  },
  subtitle: {
    marginVertical: 20
  },
  buttons: {
    flexDirection: isTablet ? 'row' : 'column-reverse',
    justifyContent: 'space-between'
  },
  passwordButton: {
    width: isTablet ? 'auto' : '100%',
    marginRight: isTablet ? 10 : 0,
    marginTop: isTablet ? 0 : 10
  },
  saveButton: {
    marginLeft: isTablet ? 10 : 0,
    alignItems: isTablet ? 'flex-end' : 'stretch'
  }
})

UserInformationForm.propTypes = {
  load: PropTypes.bool.isRequired,
  user: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  handleModal: PropTypes.func.isRequired
}

export default UserInformationForm
